import { useEffect, useRef, useState, useCallback } from "react";
import { toast } from "sonner";
import { Pencil, Trash2, Plus, Upload, X } from "lucide-react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent,
  AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const blank = {
  id: "", title: "", description: "", date: "", time: "", end_time: "",
  blocks_availability: "full_day",
  type: "private", image_url: "", cover_video_url: "", info_image_url: "",
  ticket_price: "", ticket_widget_id: "", ticket_widget_type: "embed", active: true,
};

const formatDate = (iso) =>
  iso ? new Date(`${iso}T00:00:00`).toLocaleDateString("ca-ES", { day: "2-digit", month: "short", year: "numeric" }) : "";

const fullImageUrl = (u) => {
  if (!u) return "";
  if (u.startsWith("http")) return u;
  if (u.startsWith("/api/")) return `${BACKEND_URL}${u}`;
  return u;
};

export default function AdminVenueEvents({ editId, onEditHandled }) {
  const [events, setEvents] = useState([]);
  const [fetching, setFetching] = useState(true);
  const [dialogOpen, setDialogOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(null);
  const [editing, setEditing] = useState(blank);
  const [uploading, setUploading] = useState(false);
  const fileInputRef = useRef(null);
  const coverVideoInputRef = useRef(null);
  const infoImageInputRef = useRef(null);

  const loadEvents = useCallback(async () => {
    setFetching(true);
    try {
      const { data } = await api.get("/events");
      setEvents(data);
    } catch {
      toast.error("Error carregant esdeveniments.");
    } finally {
      setFetching(false);
    }
  }, []);

  useEffect(() => { loadEvents(); }, [loadEvents]);

  useEffect(() => {
    if (!editId || fetching) return;
    const ev = events.find((e) => e.id === editId);
    if (ev) openEdit(ev);
    else toast.error("Esdeveniment no trobat.");
    onEditHandled?.();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [editId, fetching, events]);

  const openCreate = () => { setEditing(blank); setDialogOpen(true); };
  function openEdit(ev) {
    setEditing({
      ...ev, time: ev.time || "", end_time: ev.end_time || "",
      blocks_availability: ev.blocks_availability || "full_day",
      image_url: ev.image_url || "",
      cover_video_url: ev.cover_video_url || "", info_image_url: ev.info_image_url || "",
      ticket_price: ev.ticket_price ?? "", ticket_widget_id: ev.ticket_widget_id || "",
      ticket_widget_type: ev.ticket_widget_type || "embed",
      description: ev.description || "",
    });
    setDialogOpen(true);
  }

  const uploadTo = async (file, field, { video = false, maxMb } = {}) => {
    const limit = (maxMb || (video ? 60 : 8)) * 1024 * 1024;
    if (file.size > limit) {
      toast.error(`Fitxer més gran de ${maxMb || (video ? 60 : 8)}MB.`);
      return;
    }
    setUploading(true);
    try {
      const data = new FormData();
      data.append("file", file);
      const { data: result } = await api.post("/uploads", data, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      setEditing((p) => ({ ...p, [field]: result.url }));
      toast.success(video ? "Vídeo pujat." : "Imatge pujada.");
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error pujant el fitxer.");
    } finally {
      setUploading(false);
    }
  };

  const handleFileChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadTo(file, "image_url", { video: false });
    if (fileInputRef.current) fileInputRef.current.value = "";
  };

  const handleCoverVideoChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadTo(file, "cover_video_url", { video: true });
    if (coverVideoInputRef.current) coverVideoInputRef.current.value = "";
  };

  const handleInfoImageChange = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    await uploadTo(file, "info_image_url", { video: false });
    if (infoImageInputRef.current) infoImageInputRef.current.value = "";
  };

  const onSave = async (e) => {
    e.preventDefault();
    if (!editing.title || !editing.date || !editing.type) {
      toast.error("Títol, data i tipus són obligatoris.");
      return;
    }
    const payload = {
      title: editing.title, description: editing.description || "",
      date: editing.date, time: editing.time || null,
      end_time: editing.end_time || null,
      blocks_availability: editing.blocks_availability || "full_day",
      type: editing.type,
      image_url: editing.image_url || null,
      cover_video_url: editing.cover_video_url || null,
      info_image_url: editing.info_image_url || null,
      ticket_price: editing.ticket_price !== "" ? parseFloat(editing.ticket_price) : null,
      ticket_widget_id: editing.ticket_widget_id || null,
      ticket_widget_type: editing.ticket_widget_type || "embed",
      active: editing.active,
    };
    try {
      if (editing.id) {
        await api.put(`/events/${editing.id}`, payload);
        toast.success("Esdeveniment actualitzat.");
      } else {
        await api.post("/events", payload);
        toast.success("Esdeveniment creat.");
      }
      setDialogOpen(false);
      loadEvents();
    } catch (err) {
      toast.error(err?.response?.data?.detail || "Error guardant.");
    }
  };

  const onDelete = async () => {
    if (!confirmDelete) return;
    try {
      await api.delete(`/events/${confirmDelete.id}`);
      toast.success("Esdeveniment eliminat.");
      setConfirmDelete(null);
      loadEvents();
    } catch {
      toast.error("No s'ha pogut eliminar.");
    }
  };

  return (
    <div data-testid="admin-venue-events">
        <div className="flex items-center justify-between mb-8">
          <div>
            <p className="uppercase tracking-[0.28em] text-xs text-[#3e664c] mb-2">Gestió</p>
            <h1 className="font-display text-4xl tracking-tight">Esdeveniments</h1>
          </div>
          <Button onClick={openCreate} data-testid="admin-create-event"
            className="rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-[#fdfcf9] gap-2 px-6 py-5 text-sm uppercase tracking-[0.18em]">
            <Plus size={16} /> Nou esdeveniment
          </Button>
        </div>

        <div className="bg-white border border-[#eae0df] rounded-2xl overflow-hidden">
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead className="bg-[#fdfcf9] text-[#5C544F]">
                <tr>
                  <th className="text-left px-5 py-4 uppercase tracking-[0.18em] text-[10px]">Data</th>
                  <th className="text-left px-5 py-4 uppercase tracking-[0.18em] text-[10px]">Títol</th>
                  <th className="text-left px-5 py-4 uppercase tracking-[0.18em] text-[10px]">Tipus</th>
                  <th className="text-left px-5 py-4 uppercase tracking-[0.18em] text-[10px]">Preu</th>
                  <th className="text-left px-5 py-4 uppercase tracking-[0.18em] text-[10px]">Estat</th>
                  <th className="px-5 py-4" />
                </tr>
              </thead>
              <tbody data-testid="admin-events-table">
                {fetching ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-[#5C544F]">…</td></tr>
                ) : events.length === 0 ? (
                  <tr><td colSpan={6} className="px-5 py-10 text-center text-[#5C544F]">No hi ha esdeveniments.</td></tr>
                ) : (
                  events.map((ev) => (
                    <tr key={ev.id} className="border-t border-[#eae0df]" data-testid={`admin-row-${ev.id}`}>
                      <td className="px-5 py-4 whitespace-nowrap">{formatDate(ev.date)} {ev.time && `· ${ev.time}`}</td>
                      <td className="px-5 py-4 font-medium">{ev.title}</td>
                      <td className="px-5 py-4">
                        <span className={`px-2.5 py-1 rounded-full text-xs uppercase tracking-[0.18em] ${ev.type === "public" ? "bg-[#3e664c]/10 text-[#3e664c]" : "bg-[#450905]/10 text-[#450905]"}`}>
                          {ev.type === "public" ? "Públic" : "Privat"}
                        </span>
                      </td>
                      <td className="px-5 py-4">{ev.ticket_price ? `${ev.ticket_price.toFixed(2)} €` : "—"}</td>
                      <td className="px-5 py-4">{ev.active ? "Actiu" : "Inactiu"}</td>
                      <td className="px-5 py-4 text-right">
                        <div className="flex justify-end gap-2">
                          <Button size="icon" variant="ghost" onClick={() => openEdit(ev)} data-testid={`admin-edit-${ev.id}`}><Pencil size={15} /></Button>
                          <Button size="icon" variant="ghost" onClick={() => setConfirmDelete(ev)} data-testid={`admin-delete-${ev.id}`}><Trash2 size={15} className="text-[#450905]" /></Button>
                        </div>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          </div>
        </div>

      <Dialog open={dialogOpen} onOpenChange={setDialogOpen}>
        <DialogContent className="bg-white max-w-2xl" data-testid="admin-event-dialog">
          <DialogHeader>
            <DialogTitle className="font-display text-3xl tracking-tight">
              {editing.id ? "Editar esdeveniment" : "Nou esdeveniment"}
            </DialogTitle>
            <DialogDescription className="text-[#5C544F]">
              Una data només pot contenir un esdeveniment actiu.
            </DialogDescription>
          </DialogHeader>
          <form onSubmit={onSave} className="space-y-4">
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Títol</Label>
              <Input value={editing.title} onChange={(e) => setEditing({ ...editing, title: e.target.value })}
                data-testid="admin-input-title" required className="mt-1" />
            </div>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Data</Label>
                <Input type="date" value={editing.date}
                  onChange={(e) => setEditing({ ...editing, date: e.target.value })}
                  data-testid="admin-input-date" required className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Inici</Label>
                <Input type="time" value={editing.time}
                  onChange={(e) => setEditing({ ...editing, time: e.target.value })}
                  data-testid="admin-input-time" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Fi</Label>
                <Input type="time" value={editing.end_time}
                  onChange={(e) => setEditing({ ...editing, end_time: e.target.value })}
                  data-testid="admin-input-end-time" className="mt-1" />
              </div>
              <div>
                <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Tipus</Label>
                <Select value={editing.type} onValueChange={(v) => setEditing({ ...editing, type: v })}>
                  <SelectTrigger className="mt-1" data-testid="admin-input-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="private">Privat</SelectItem>
                    <SelectItem value="public">Públic</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Franja que bloqueja al calendari</Label>
              <Select value={editing.blocks_availability} onValueChange={(v) => setEditing({ ...editing, blocks_availability: v })}>
                <SelectTrigger className="mt-1" data-testid="admin-input-blocks"><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="full_day">Tot el dia</SelectItem>
                  <SelectItem value="morning">Només matí (fins 17h)</SelectItem>
                  <SelectItem value="evening">Només nit (a partir de 17h)</SelectItem>
                  <SelectItem value="custom">Franja concreta (usa hores d&apos;inici/fi)</SelectItem>
                  <SelectItem value="none">No bloqueja (només informatiu)</SelectItem>
                </SelectContent>
              </Select>
              <p className="text-[10px] text-[#5C544F]/70 mt-1 uppercase tracking-[0.18em]">
                Independent de l&apos;horari real. Ex: sopar a la nit però blocatge tot el dia per preparatius.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Preu entrada (€)</Label>
              <Input type="number" step="0.01" value={editing.ticket_price}
                onChange={(e) => setEditing({ ...editing, ticket_price: e.target.value })}
                placeholder="Només esdeveniments públics"
                data-testid="admin-input-price" className="mt-1" />
            </div>

            {/* Cover: image or video */}
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Portada (imatge o vídeo curt)</Label>
              <div className="mt-2 flex items-center gap-4">
                {editing.cover_video_url ? (
                  <div className="relative h-24 w-24 rounded-xl overflow-hidden border border-[#eae0df] bg-[#1c2421]" data-testid="admin-cover-video-preview">
                    <video src={fullImageUrl(editing.cover_video_url)} muted playsInline className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, cover_video_url: "" })}
                      className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#3e664c] text-white flex items-center justify-center"
                      data-testid="admin-cover-video-remove"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : editing.image_url ? (
                  <div className="relative h-24 w-24 rounded-xl overflow-hidden border border-[#eae0df]" data-testid="admin-image-preview">
                    <img src={fullImageUrl(editing.image_url)} alt="preview" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, image_url: "" })}
                      className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#3e664c] text-white flex items-center justify-center"
                      data-testid="admin-image-remove"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="h-24 w-24 rounded-xl border border-dashed border-[#eae0df] flex items-center justify-center text-[#5C544F]/60">
                    <Upload size={20} />
                  </div>
                )}
                <div className="flex-1 space-y-2">
                  <div className="flex flex-wrap gap-2">
                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/png,image/jpeg,image/webp,image/gif"
                      onChange={handleFileChange}
                      className="hidden"
                      data-testid="admin-image-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => fileInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="admin-image-upload-btn"
                      className="gap-2"
                    >
                      <Upload size={14} />
                      {uploading ? "Pujant…" : "Imatge"}
                    </Button>
                    <input
                      ref={coverVideoInputRef}
                      type="file"
                      accept="video/mp4,video/webm,video/quicktime"
                      onChange={handleCoverVideoChange}
                      className="hidden"
                      data-testid="admin-cover-video-file-input"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      onClick={() => coverVideoInputRef.current?.click()}
                      disabled={uploading}
                      data-testid="admin-cover-video-upload-btn"
                      className="gap-2"
                    >
                      <Upload size={14} />
                      {uploading ? "Pujant…" : "Vídeo"}
                    </Button>
                  </div>
                  <p className="text-[10px] text-[#5C544F]/70 uppercase tracking-[0.18em]">
                    Imatges: JPG/PNG/WEBP · &lt; 8MB &nbsp;·&nbsp; Vídeos: MP4/WEBM/MOV · &lt; 60MB
                  </p>
                  <p className="text-[10px] text-[#5C544F]/70">
                    Si hi ha vídeo, s&apos;usa com a portada. Sinó, es fa servir la imatge.
                  </p>
                </div>
              </div>
            </div>

            {/* Info image inside event */}
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Imatge d&apos;info (dins de l&apos;esdeveniment)</Label>
              <div className="mt-2 flex items-center gap-4">
                {editing.info_image_url ? (
                  <div className="relative h-24 w-24 rounded-xl overflow-hidden border border-[#eae0df]" data-testid="admin-info-image-preview">
                    <img src={fullImageUrl(editing.info_image_url)} alt="info" className="w-full h-full object-cover" />
                    <button
                      type="button"
                      onClick={() => setEditing({ ...editing, info_image_url: "" })}
                      className="absolute -top-1 -right-1 w-6 h-6 rounded-full bg-[#3e664c] text-white flex items-center justify-center"
                      data-testid="admin-info-image-remove"
                    >
                      <X size={12} />
                    </button>
                  </div>
                ) : (
                  <div className="h-24 w-24 rounded-xl border border-dashed border-[#eae0df] flex items-center justify-center text-[#5C544F]/60">
                    <Upload size={20} />
                  </div>
                )}
                <div className="flex-1">
                  <input
                    ref={infoImageInputRef}
                    type="file"
                    accept="image/png,image/jpeg,image/webp,image/gif"
                    onChange={handleInfoImageChange}
                    className="hidden"
                    data-testid="admin-info-image-file-input"
                  />
                  <Button
                    type="button"
                    variant="outline"
                    onClick={() => infoImageInputRef.current?.click()}
                    disabled={uploading}
                    data-testid="admin-info-image-upload-btn"
                    className="gap-2"
                  >
                    <Upload size={14} />
                    {uploading ? "Pujant…" : "Pujar imatge info"}
                  </Button>
                  <p className="text-[10px] text-[#5C544F]/70 mt-2 uppercase tracking-[0.18em]">
                    Ex: pòster amb detalls extra
                  </p>
                </div>
              </div>
            </div>

            {/* TicketsCandy widget */}
            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">TicketsCandy widget</Label>
              <Textarea
                value={editing.ticket_widget_id}
                onChange={(e) => {
                  const raw = e.target.value;
                  // Detect widget type + id from any of the known TicketsCandy snippet patterns
                  let extracted = "";
                  let detectedType = editing.ticket_widget_type || "embed";
                  const checkoutMatch = raw.match(/tc-checkout-container-(\d+)|eventId:\s*['"]?(\d+)['"]?/);
                  const embedMatch = raw.match(/tc-container-iframe-(\d+)|ticketscandy\.com\/e\/(\d+)/);
                  if (checkoutMatch) {
                    extracted = checkoutMatch[1] || checkoutMatch[2];
                    detectedType = "checkout";
                  } else if (embedMatch) {
                    extracted = embedMatch[1] || embedMatch[2];
                    detectedType = "embed";
                  } else {
                    extracted = raw.replace(/\D/g, "");
                  }
                  setEditing({
                    ...editing,
                    ticket_widget_id: extracted || raw,
                    ticket_widget_type: detectedType,
                  });
                }}
                data-testid="admin-input-widget"
                placeholder="Enganxa aquí l'HTML sencer del widget de TicketsCandy (embed o checkout) o només el número"
                rows={4}
                className="mt-1 font-mono text-xs"
              />
              <div className="mt-2 flex flex-wrap items-center gap-3">
                <Label className="text-[10px] uppercase tracking-[0.18em] text-[#5C544F]">Tipus</Label>
                <Select
                  value={editing.ticket_widget_type}
                  onValueChange={(v) => setEditing({ ...editing, ticket_widget_type: v })}
                >
                  <SelectTrigger className="w-40 h-8 text-xs" data-testid="admin-input-widget-type"><SelectValue /></SelectTrigger>
                  <SelectContent>
                    <SelectItem value="embed">Embed (pàgina del event)</SelectItem>
                    <SelectItem value="checkout">Checkout (compra directa)</SelectItem>
                  </SelectContent>
                </Select>
                {editing.ticket_widget_id && /^\d+$/.test(editing.ticket_widget_id) && (
                  <span className="text-[10px] text-[#3e664c] uppercase tracking-[0.18em]">
                    ✓ {editing.ticket_widget_type === "checkout" ? "Checkout" : "Embed"} · ID {editing.ticket_widget_id}
                  </span>
                )}
              </div>
              <p className="text-[10px] text-[#5C544F]/70 mt-2 uppercase tracking-[0.18em]">
                Detecta automàticament el tipus segons l&apos;HTML enganxat. Pots forçar-ho amb el selector.
              </p>
            </div>

            <div>
              <Label className="text-xs uppercase tracking-[0.18em] text-[#5C544F]">Descripció</Label>
              <Textarea value={editing.description} onChange={(e) => setEditing({ ...editing, description: e.target.value })}
                data-testid="admin-input-description" rows={4} className="mt-1" />
            </div>
            <DialogFooter>
              <Button type="button" variant="outline" onClick={() => setDialogOpen(false)} data-testid="admin-dialog-cancel">Cancel·lar</Button>
              <Button type="submit" className="rounded-full bg-[#3e664c] hover:bg-[#2e5949] text-white px-6" data-testid="admin-dialog-save">
                {editing.id ? "Guardar canvis" : "Crear esdeveniment"}
              </Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>

      <AlertDialog open={!!confirmDelete} onOpenChange={(o) => !o && setConfirmDelete(null)}>
        <AlertDialogContent data-testid="admin-confirm-delete">
          <AlertDialogHeader>
            <AlertDialogTitle className="font-display text-2xl">Eliminar esdeveniment?</AlertDialogTitle>
            <AlertDialogDescription>
              Aquesta acció no es pot desfer. S&apos;eliminarà {confirmDelete?.title}.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel data-testid="admin-confirm-cancel">Cancel·lar</AlertDialogCancel>
            <AlertDialogAction onClick={onDelete} data-testid="admin-confirm-ok" className="bg-[#450905] hover:bg-[#2c0603]">
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
